import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Button, 
  Divider, 
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  TextField,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Switch,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Comment as CommentIcon,
  Build as BuildIcon,
  SwapHoriz as SwapHorizIcon,
  Handyman as HandymanIcon,
  MoreHoriz as MoreHorizIcon,
  MoneyOff as MoneyOffIcon,
  Euro as EuroIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { task, car as carApi, user as userApi } from '../../utils/api';
import { TASK_STATUS, TASK_PRIORITY, TASK_TYPE } from '../../constants/task';
import { useAuth } from '../../components/auth/AuthModule';
import { format } from 'date-fns';

const TaskDetailPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['workshop', 'common']);
  const { user } = useAuth();
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState({});
  const [actualHours, setActualHours] = useState('');
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [users, setUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
      fetchUsers();
    }
  }, [taskId]);

  useEffect(() => {
    console.log('Users state updated:', users);
  }, [users]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const data = await task.getById(taskId);
      setTaskData(data);
      
      // Debug comment data structure
      if (data.notes && data.notes.length > 0) {
        console.log('Comment data structure:', data.notes);
        data.notes.forEach((note, index) => {
          console.log(`Comment ${index + 1} user info:`, note.addedBy);
          console.log(`Comment ${index + 1} content:`, note.content);
          console.log(`Comment ${index + 1} date:`, note.addedAt);
        });
      }
      
      // Debug car data
      if (data.car) {
        console.log('Car data in task:', data.car);
        console.log('Car ID:', data.car._id);
        console.log('Car name:', data.car.name);
        console.log('Car brand:', data.car.brand);
        console.log('Car model:', data.car.model);
        console.log('Car year:', data.car.year);
        console.log('Car image URL:', data.car.imageUrl);
        
        // Debug car owner information
        if (data.car.user) {
          console.log('Car owner:', data.car.user);
          console.log('Car owner ID:', data.car.user._id);
          console.log('Car owner ID type:', typeof data.car.user._id);
          console.log('Current user ID:', user?._id);
          console.log('Current user ID type:', typeof user?._id);
          console.log('Is car owner check:', 
            user?._id === data.car.user._id || 
            user?._id === data.car.user.toString() || 
            user?._id?.toString() === data.car.user._id || 
            user?._id?.toString() === data.car.user.toString()
          );
        } else {
          console.log('No car owner information found');
        }
        
        // Check if car data is a populated object or just an ID
        console.log('Is car data fully populated:', 
          typeof data.car === 'object' && 
          (data.car.name !== undefined || data.car.brand !== undefined || data.car.model !== undefined)
        );
      } else {
        console.log('No car data found in task');
      }
      
      // Debug financial tracking data
      if (data.financialTracking) {
        console.log('Financial tracking data:', data.financialTracking);
        
        if (data.financialTracking.paidBy) {
          console.log('Paid by:', data.financialTracking.paidBy);
          console.log('Paid by ID:', data.financialTracking.paidBy._id);
          console.log('Paid by ID type:', typeof data.financialTracking.paidBy._id);
          console.log('Is payer check:', 
            user?._id === data.financialTracking.paidBy._id || 
            user?._id === data.financialTracking.paidBy.toString() || 
            user?._id?.toString() === data.financialTracking.paidBy._id || 
            user?._id?.toString() === data.financialTracking.paidBy.toString()
          );
        }
        
        if (data.financialTracking.owedBy) {
          console.log('Owed by:', data.financialTracking.owedBy);
          console.log('Owed by ID:', data.financialTracking.owedBy._id);
        }
      }
      
      if (data.actualHours) {
        setActualHours(data.actualHours.toString());
      }
      
      setEditedTask({
        title: data.title || '',
        description: data.description || '',
        status: data.status || TASK_STATUS.PENDING,
        priority: data.priority || TASK_PRIORITY.MEDIUM,
        type: data.type || TASK_TYPE.MAINTENANCE,
        estimatedHours: data.estimatedHours || '',
        actualHours: data.actualHours || '',
        dueDate: data.dueDate || '',
        assignedTo: data.assignedTo?._id || '',
        financialTracking: data.financialTracking || {
          repairCost: 0,
          paidBy: null,
          isPaid: false,
          owedBy: null
        }
      });
      
    } catch (error) {
      console.error('Error fetching task details:', error);
      setError(t('taskDetail.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const fetchedUsers = await userApi.getUsers();
      // Ensure we have an array of users
      const usersArray = Array.isArray(fetchedUsers) ? fetchedUsers : [];
      console.log('Fetched users:', usersArray);
      setUsers(usersArray);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('error.loadUsers'));
      // Initialize with empty array on error
      setUsers([]);
    }
  };

  const handleGoBack = () => {
    navigate('/workshop');
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await task.update(taskId, { 
        status: newStatus,
        ...(newStatus === TASK_STATUS.COMPLETED && actualHours ? { actualHours: Number(actualHours) } : {})
      });
      toast.success(t('success.taskUpdated'));
      fetchTaskDetails();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('error.updateTask'));
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      // Initialize editedTask with current task data when entering edit mode
      setEditedTask({
        title: taskData.title || '',
        description: taskData.description || '',
        status: taskData.status || TASK_STATUS.PENDING,
        priority: taskData.priority || TASK_PRIORITY.MEDIUM,
        type: taskData.type || TASK_TYPE.MAINTENANCE,
        estimatedHours: taskData.estimatedHours || '',
        actualHours: taskData.actualHours || '',
        dueDate: taskData.dueDate || '',
        assignedTo: taskData.assignedTo?._id || '',
        financialTracking: taskData.financialTracking || {
          repairCost: 0,
          paidBy: null,
          isPaid: false,
          owedBy: null
        }
      });
      
      // Log the current state of the editedTask before entering edit mode
      console.log('Entering edit mode with data:', taskData);
      console.log('Financial tracking data:', taskData.financialTracking);
      console.log('Paid by:', taskData.financialTracking?.paidBy);
      console.log('Owed by:', taskData.financialTracking?.owedBy);
      console.log('Available users:', users);
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input changed:', name, value);
    
    // Handle nested properties (e.g., financialTracking.repairCost)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      console.log('Updating nested property:', parent, child, value);
      setEditedTask(prev => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
        console.log('Updated task:', updated);
        return updated;
      });
    } else if (name === 'dueDate') {
      // Convert date string to ISO format for the API
      const dateValue = value ? new Date(value).toISOString() : '';
      setEditedTask(prev => ({
        ...prev,
        [name]: dateValue
      }));
    } else {
      setEditedTask(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation based on backend model
    if (!editedTask.title?.trim()) {
      newErrors.title = t('validation.required');
    }
    
    if (!editedTask.description?.trim()) {
      newErrors.description = t('validation.required');
    }
    
    if (!editedTask.assignedTo) {
      newErrors.assignedTo = t('validation.required');
    }

    // Number field validations - only validate if they have a value
    if (editedTask.estimatedHours !== undefined && editedTask.estimatedHours !== '' && editedTask.estimatedHours < 0) {
      newErrors.estimatedHours = t('validation.positiveNumber');
    }

    if (editedTask.actualHours !== undefined && editedTask.actualHours !== '' && editedTask.actualHours < 0) {
      newErrors.actualHours = t('validation.positiveNumber');
    }

    // Financial tracking validations
    if (editedTask.financialTracking?.repairCost !== undefined && 
        editedTask.financialTracking.repairCost !== '' && 
        editedTask.financialTracking.repairCost < 0) {
      newErrors['financialTracking.repairCost'] = t('validation.positiveNumber');
    }

    // If repair cost is set, paidBy and owedBy should be set
    if (editedTask.financialTracking?.repairCost > 0) {
      if (!editedTask.financialTracking.paidBy) {
        newErrors['financialTracking.paidBy'] = t('validation.required');
      }
      if (!editedTask.financialTracking.owedBy) {
        newErrors['financialTracking.owedBy'] = t('validation.required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      toast.error(t('validation.fixErrors'));
      return;
    }

    try {
      // Clean up the data before sending to server
      const cleanedTask = {
        ...editedTask,
        // Convert empty string to undefined for optional number fields
        estimatedHours: editedTask.estimatedHours === '' ? undefined : Number(editedTask.estimatedHours),
        actualHours: editedTask.actualHours === '' ? undefined : Number(editedTask.actualHours),
        // Clean up financial tracking
        financialTracking: editedTask.financialTracking ? {
          ...editedTask.financialTracking,
          repairCost: editedTask.financialTracking.repairCost === '' ? 
            undefined : 
            Number(editedTask.financialTracking.repairCost)
        } : undefined
      };

      await task.update(taskId, cleanedTask);
      toast.success(t('success.taskUpdated'));
      setIsEditing(false);
      fetchTaskDetails();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(t('error.updateTask'));
    }
  };

  const handleDeleteTask = async () => {
    if (window.confirm(t('common:confirmDelete'))) {
      try {
        await task.delete(taskId);
        toast.success(t('success.taskDeleted'));
        navigate('/workshop');
      } catch (error) {
        console.error('Error deleting task:', error);
        toast.error(t('error.deleteTask'));
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return 'warning';
      case TASK_STATUS.IN_PROGRESS:
        return 'info';
      case TASK_STATUS.COMPLETED:
        return 'success';
      case TASK_STATUS.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return <AccessTimeIcon />;
      case TASK_STATUS.IN_PROGRESS:
        return <EngineeringIcon />;
      case TASK_STATUS.COMPLETED:
        return <CheckCircleIcon />;
      case TASK_STATUS.CANCELLED:
        return <CancelIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  // Function to get the complete status chip
  const getStatusChip = (status) => {
    return (
      <Chip 
        icon={getStatusIcon(status)} 
        label={t(`status.${status}`)} 
        color={getStatusColor(status)} 
        variant="filled" 
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TASK_PRIORITY.LOW:
        return 'success';
      case TASK_PRIORITY.MEDIUM:
        return 'info';
      case TASK_PRIORITY.HIGH:
        return 'warning';
      case TASK_PRIORITY.URGENT:
        return 'error';
      default:
        return 'default';
    }
  };

  const isTaskAssignedToCurrentUser = () => {
    return user && taskData && taskData.assignedTo && user._id === taskData.assignedTo._id;
  };

  const isTaskCreator = () => {
    return user && taskData && taskData.createdBy && user._id === taskData.createdBy._id;
  };

  const isCarOwner = () => {
    if (!user || !taskData || !taskData.car || !taskData.car.user) return false;
    
    // Compare as strings to avoid reference comparison issues
    return user._id === taskData.car.user._id || 
           user._id === taskData.car.user.toString() || 
           user._id.toString() === taskData.car.user._id || 
           user._id.toString() === taskData.car.user.toString();
  };

  const isAdmin = () => {
    return user && user.isAdmin;
  };

  const canEditTask = () => {
    return isTaskAssignedToCurrentUser() || isTaskCreator() || isCarOwner() || isAdmin();
  };

  const canDeleteTask = () => {
    return isTaskCreator() || isCarOwner() || isAdmin();
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    
    try {
      setSubmittingComment(true);
      await task.addNote(taskId, comment.trim());
      toast.success(t('success.commentAdded'));
      setComment('');
      fetchTaskDetails(); // Refresh to get the new comment
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(t('error.addComment'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCommentKeyDown = (e) => {
    // Submit on Enter (but not with Shift key for new lines)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleImageError = (userId) => {
    setImageErrors(prev => ({
      ...prev,
      [userId]: true
    }));
  };

  const navigateToCarDetails = () => {
    if (taskData?.car?._id) {
      console.log('Navigating to car details:', taskData.car._id);
      navigate(`/cars/${taskData.car._id}`);
    } else {
      console.log('Cannot navigate to car details - missing car ID');
      toast.error(t('errors.carDetailsNotAvailable', 'Car details not available'));
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case TASK_TYPE.MAINTENANCE:
        return <BuildIcon />;
      case TASK_TYPE.REPLACEMENT:
        return <SwapHorizIcon />;
      case TASK_TYPE.FIX:
        return <HandymanIcon />;
      case TASK_TYPE.OTHER:
        return <MoreHorizIcon />;
      default:
        return <BuildIcon />;
    }
  };

  const handlePaymentStatusChange = async () => {
    try {
      setUpdatingPayment(true);
      
      // Get the current financial tracking data
      const currentFinancialTracking = taskData.financialTracking || {
        repairCost: 0,
        paidBy: null,
        isPaid: false,
        owedBy: null
      };
      
      // Toggle the isPaid status
      const updatedFinancialTracking = {
        ...currentFinancialTracking,
        isPaid: !currentFinancialTracking.isPaid
      };
      
      // Update the task with the new financial tracking data
      await task.update(taskId, { 
        financialTracking: updatedFinancialTracking 
      });
      
      // Add a comment about the payment status change with information about who made the change
      let commentText;
      if (updatedFinancialTracking.isPaid) {
        if (isCarOwner()) {
          commentText = t('taskDetail.financialTracking.paymentMadeByOwnerComment', { username: user.name || user.username });
        } else if (isPayer()) {
          commentText = t('taskDetail.financialTracking.paymentConfirmedByPayerComment', { username: user.name || user.username });
        } else if (isOwedBy()) {
          commentText = t('taskDetail.financialTracking.paymentMadeByDebtorComment', { username: user.name || user.username });
        } else {
          commentText = t('taskDetail.financialTracking.paymentMadeComment');
        }
      } else {
        commentText = t('taskDetail.financialTracking.paymentRevertedComment', { username: user.name || user.username });
      }
      
      // Add the comment
      await task.addNote(taskId, commentText);
      
      // Show success message
      toast.success(
        updatedFinancialTracking.isPaid
          ? t('success.paymentMarkedAsPaid')
          : t('success.paymentMarkedAsUnpaid')
      );
      
      // Refresh task details
      fetchTaskDetails();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error(t('error.updatePaymentStatus'));
    } finally {
      setUpdatingPayment(false);
    }
  };
  
  const isPayer = () => {
    if (!user || !taskData || !taskData.financialTracking || !taskData.financialTracking.paidBy) return false;
    
    // Compare as strings to avoid reference comparison issues
    return user._id === taskData.financialTracking.paidBy._id || 
           user._id === taskData.financialTracking.paidBy.toString() || 
           user._id.toString() === taskData.financialTracking.paidBy._id || 
           user._id.toString() === taskData.financialTracking.paidBy.toString();
  };
  
  const isOwedBy = () => {
    if (!user || !taskData || !taskData.financialTracking || !taskData.financialTracking.owedBy) return false;
    
    // Compare as strings to avoid reference comparison issues
    return user._id === taskData.financialTracking.owedBy._id || 
           user._id === taskData.financialTracking.owedBy.toString() || 
           user._id.toString() === taskData.financialTracking.owedBy._id || 
           user._id.toString() === taskData.financialTracking.owedBy.toString();
  };
  
  const canMarkAsPaid = () => {
    // The car owner, the person who paid, and the person who owes can all mark a task as paid
    return (isCarOwner() || isPayer() || isOwedBy()) && 
           taskData && 
           taskData.financialTracking && 
           taskData.financialTracking.repairCost > 0 && 
           !taskData.financialTracking.isPaid;
  };
  
  const canMarkAsUnpaid = () => {
    // Only the person who paid can mark a task as unpaid
    return isPayer() && 
           taskData && 
           taskData.financialTracking && 
           taskData.financialTracking.isPaid;
  };

  const renderFinancialTracking = () => {
    if (!taskData || !taskData.financialTracking) return null;
    
    const { repairCost, labourCost, paidBy, isPaid, owedBy } = taskData.financialTracking;
    const totalCost = (repairCost || 0) + (labourCost || 0);
    
    if (!repairCost && !labourCost) return null;
    
    // Debug permission checks
    console.log('Financial tracking permissions:');
    console.log('Is car owner:', isCarOwner());
    console.log('Is payer:', isPayer());
    console.log('Is owed by:', isOwedBy());
    console.log('Can mark as paid:', canMarkAsPaid());
    console.log('Can mark as unpaid:', canMarkAsUnpaid());
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box mb={2} display="flex" alignItems="center">
            <Chip 
              icon={isPaid ? <CheckCircleIcon /> : <MoneyOffIcon />}
              label={isPaid ? t('taskDetail.financialTracking.paid') : t('taskDetail.financialTracking.pending')}
              color={isPaid ? "success" : "warning"}
              variant={isPaid ? "filled" : "outlined"}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            {repairCost > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('taskDetail.financialTracking.repairCost')}:
                </Typography>
                <Typography variant="subtitle1" color="primary">
                  {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(repairCost)}
                </Typography>
              </Box>
            )}
            
            {labourCost > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('taskDetail.financialTracking.labourCost')}:
                </Typography>
                <Typography variant="subtitle1" color="primary">
                  {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(labourCost)}
                </Typography>
              </Box>
            )}
            
            {(repairCost > 0 || labourCost > 0) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('taskDetail.financialTracking.totalCost')}:
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(totalCost)}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <PaymentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('taskDetail.financialTracking.paidBy')}:
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Avatar 
                  src={paidBy?.imageUrl} 
                  alt={paidBy?.name || paidBy?.username || t('common.unknown')}
                  sx={{ width: 24, height: 24, mr: 1 }}
                >
                  {paidBy?.name ? paidBy.name[0].toUpperCase() : '?'}
                </Avatar>
                <Typography variant="body1">
                  {paidBy?.name || paidBy?.username || t('common.unknown')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <MoneyOffIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('taskDetail.financialTracking.owedBy')}:
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Avatar 
                  src={owedBy?.imageUrl} 
                  alt={owedBy?.name || owedBy?.username || t('common.unknown')}
                  sx={{ width: 24, height: 24, mr: 1 }}
                >
                  {owedBy?.name ? owedBy.name[0].toUpperCase() : '?'}
                </Avatar>
                <Typography variant="body1">
                  {owedBy?.name || owedBy?.username || t('common.unknown')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box display="flex" mt={2} alignItems="center" justifyContent="space-between">
            {(canMarkAsPaid() || canMarkAsUnpaid()) && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isPaid}
                    onChange={handlePaymentStatusChange}
                    disabled={updatingPayment}
                    color="success"
                  />
                }
                label={isPaid ? t('taskDetail.financialTracking.paid') : t('taskDetail.financialTracking.markAsPaid')}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const handleCalculateLabourCost = async () => {
    try {
      const updatedTask = await task.calculateLabourCost(taskId);
      setTaskData(updatedTask);
      toast.success(t('success.labourCostCalculated'));
    } catch (error) {
      console.error('Error calculating labour cost:', error);
      toast.error(error.response?.data?.message || t('error.calculateLabourCost'));
    }
  };

  const renderLabourCostSection = () => {
    if (!isTaskAssignedToCurrentUser() || taskData.status !== TASK_STATUS.COMPLETED) return null;
    
    const hasLabourCost = taskData.financialTracking?.labourCost > 0;
    const hasActualHours = taskData.actualHours > 0;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('taskDetail.labourCost.title')}
        </Typography>
        {hasLabourCost ? (
          <Typography variant="body2" color="success.main">
            {t('taskDetail.labourCost.alreadyCalculated')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCalculateLabourCost}
              disabled={!hasActualHours}
              startIcon={<EuroIcon />}
            >
              {t('taskDetail.labourCost.calculate')}
            </Button>
            {!hasActualHours && (
              <Typography variant="caption" color="error">
                {t('taskDetail.labourCost.setActualHours')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            {t('common:back')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!taskData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">{t('taskDetail.notFound')}</Alert>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            {t('common:back')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'flex-end' }}>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isTaskAssignedToCurrentUser() && (
              <>
                {taskData.status !== TASK_STATUS.COMPLETED && (
                  <Button 
                    variant="contained" 
                    color="success" 
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleStatusChange(TASK_STATUS.COMPLETED)}
                    sx={{ ml: 1 }}
                  >
                    {t('common.complete')}
                  </Button>
                )}
                {taskData.status === TASK_STATUS.COMPLETED && (
                  <Button 
                    variant="contained" 
                    color="warning" 
                    startIcon={<EngineeringIcon />}
                    onClick={() => handleStatusChange(TASK_STATUS.IN_PROGRESS)}
                    sx={{ ml: 1 }}
                  >
                    {t('common.reopen')}
                  </Button>
                )}
              </>
            )} 
            
            {canEditTask() && (
              <Button
                variant="contained"
                color="primary"
                startIcon={isEditing ? <ArrowBackIcon /> : <EditIcon />}
                onClick={handleEditToggle}
                sx={{ ml: 1 }}
              >
                {isEditing ? t('common.cancel') : t('common.edit')}
              </Button>
            )}

            {isEditing && (
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 1 }}
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
              >
                {t('common.save')}
              </Button>
            )}

          
          {canDeleteTask() && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          {t('common.delete')}
                        </Button>
                      )}
            
            </Box>
        </Box>

        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              

            <Typography variant="h6" component="h1" mb={2}>
              {taskData.title}
            </Typography>

              {isEditing ? (
                <Box>
                  <TextField
                    fullWidth
                    required
                    label={t('createTask.fields.title')}
                    name="title"
                    value={editedTask.title}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    error={!!errors.title}
                    helperText={errors.title}
                  />
                  <TextField
                    fullWidth
                    required
                    label={t('createTask.fields.description')}
                    name="description"
                    value={editedTask.description}
                    onChange={handleInputChange}
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={8}
                    error={!!errors.description}
                    helperText={errors.description}
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      } 
                    }}
                  />
                  
                  {/* Task Details Section */}
                  <Box mt={4}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                      {t('taskDetail.details')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="normal" error={!!errors.status}>
                          <InputLabel>{t('createTask.fields.status')}</InputLabel>
                          <Select
                            name="status"
                            value={editedTask.status || ''}
                            onChange={handleInputChange}
                            label={t('createTask.fields.status')}
                          >
                            {Object.values(TASK_STATUS).map((status) => (
                              <MenuItem key={status} value={status}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {getStatusIcon(status)}
                                  <Box sx={{ ml: 1 }}>{t(`status.${status}`)}</Box>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.status && (
                            <FormHelperText>{errors.status}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth margin="normal" error={!!errors.assignedTo} required>
                          <InputLabel>{t('createTask.fields.assignedTo')}</InputLabel>
                          <Select
                            name="assignedTo"
                            value={editedTask.assignedTo || ''}
                            onChange={handleInputChange}
                            label={t('createTask.fields.assignedTo')}
                          >
                            {users.map((user) => (
                              <MenuItem key={user._id} value={user._id}>
                                {user.name || user.username}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.assignedTo && (
                            <FormHelperText>{errors.assignedTo}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('createTask.fields.dueDate')}
                          name="dueDate"
                          type="date"
                          value={editedTask.dueDate ? new Date(editedTask.dueDate).toISOString().split('T')[0] : ''}
                          onChange={handleInputChange}
                          margin="normal"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.dueDate}
                          helperText={errors.dueDate}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('createTask.fields.estimatedHours')}
                          name="estimatedHours"
                          type="number"
                          value={editedTask.estimatedHours || ''}
                          onChange={handleInputChange}
                          margin="normal"
                          variant="outlined"
                          InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                          error={!!errors.estimatedHours}
                          helperText={errors.estimatedHours}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('taskDetail.actualHours')}
                          name="actualHours"
                          type="number"
                          value={editedTask.actualHours || ''}
                          onChange={handleInputChange}
                          margin="normal"
                          variant="outlined"
                          InputProps={{ 
                            inputProps: { 
                              min: 0, 
                              step: 0.5 
                            }
                          }}
                          error={!!errors.actualHours}
                          helperText={errors.actualHours}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Financial Tracking Section */}
                  <Box mt={4}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                      {t('createTask.financialTracking.title')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t('createTask.financialTracking.repairCost')}
                          name="financialTracking.repairCost"
                          type="number"
                          value={editedTask.financialTracking?.repairCost || ''}
                          onChange={handleInputChange}
                          margin="normal"
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EuroIcon />
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          error={!!errors['financialTracking.repairCost']}
                          helperText={errors['financialTracking.repairCost']}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl 
                          fullWidth 
                          margin="normal" 
                          error={!!errors['financialTracking.paidBy']}
                        >
                          <InputLabel>{t('createTask.financialTracking.paidBy')}</InputLabel>
                          <Select
                            name="financialTracking.paidBy"
                            value={editedTask.financialTracking?.paidBy?._id || editedTask.financialTracking?.paidBy || ''}
                            onChange={handleInputChange}
                            label={t('createTask.financialTracking.paidBy')}
                          >
                            {users.map((user) => (
                              <MenuItem key={user._id} value={user._id}>
                                {user.name || user.username}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors['financialTracking.paidBy'] && (
                            <FormHelperText>{errors['financialTracking.paidBy']}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl 
                          fullWidth 
                          margin="normal"
                          error={!!errors['financialTracking.owedBy']}
                        >
                          <InputLabel>{t('createTask.financialTracking.owedBy')}</InputLabel>
                          <Select
                            name="financialTracking.owedBy"
                            value={editedTask.financialTracking?.owedBy?._id || editedTask.financialTracking?.owedBy || ''}
                            onChange={handleInputChange}
                            label={t('createTask.financialTracking.owedBy')}
                          >
                            {users.map((user) => (
                              <MenuItem key={user._id} value={user._id}>
                                {user.name || user.username}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors['financialTracking.owedBy'] && (
                            <FormHelperText>{errors['financialTracking.owedBy']}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography 
                    variant="body1" 
                    component="pre"
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      mb: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {taskData.description}
                  </Typography>
                </Box>
              )}

              
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={!imageErrors[taskData.assignedTo?._id] && taskData.assignedTo?.imageUrl ? taskData.assignedTo.imageUrl : ''}
                        onError={() => handleImageError(taskData.assignedTo?._id)}
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1,
                          bgcolor: 'primary.main'
                        }}
                      >
                        {taskData.assignedTo?.name 
                          ? taskData.assignedTo.name[0].toUpperCase() 
                          : taskData.assignedTo?.username 
                            ? taskData.assignedTo.username[0].toUpperCase()
                            : '?'}
                      </Avatar>
                      <Typography variant="body2">
                      <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>{t('carView.assignedTo')}</span>
                       
                        <br/>{taskData.assignedTo?.name || taskData.assignedTo?.username || t('common.unknown')}
                      </Typography>
                    </Box>
                  </Box>
                  {taskData.car && taskData.car._id && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: 1
                          }
                        }}
                        onClick={navigateToCarDetails}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* Car Image */}
                          <Box 
                            sx={{ 
                              width: 80, 
                              height: 60, 
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                                boxShadow: 1
                              }
                            }}
                          >
                            {!imageErrors[`car-${taskData.car._id}`] && taskData.car.imageUrl ? (
                              <img 
                                src={taskData.car.imageUrl} 
                                alt={taskData.car.name || taskData.car.brand || t('common.car')}
                                onError={() => setImageErrors(prev => ({
                                  ...prev,
                                  [`car-${taskData.car._id}`]: true
                                }))}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover' 
                                }}
                              />
                            ) : (
                              <Box sx={{ color: 'text.secondary', fontSize: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Box>🚗</Box>
                                {taskData.car.brand && (
                                  <Typography variant="caption" sx={{ fontSize: '0.6rem', textAlign: 'center' }}>
                                    {taskData.car.brand}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          {/* Car Details */}
                          <Box 
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                            onClick={navigateToCarDetails}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {taskData.car.name || 
                               (taskData.car.brand && taskData.car.model ? 
                                `${taskData.car.brand} ${taskData.car.model}` : 
                                t('common.unnamed'))}
                            </Typography>
                            {!taskData.car.name && taskData.car.model && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {taskData.car.model}
                              </Typography>
                            )}
                            {taskData.car.year && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {taskData.car.year}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  
                  {/* Status and Due Date - moved from main content */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getStatusChip(taskData.status)}
                      {taskData.dueDate && (
                        <Chip 
                          icon={<CalendarTodayIcon />}
                          label={new Date(taskData.dueDate).toLocaleDateString()}
                          variant="outlined"
                          color={new Date(taskData.dueDate) < new Date() && taskData.status !== TASK_STATUS.COMPLETED ? 'error' : 'default'}
                          size="medium"
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Priority and Type */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label={t(`priority.${taskData.priority}`)} 
                        color={getPriorityColor(taskData.priority)} 
                        size="small" 
                      />
                      <Chip 
                        icon={getTaskTypeIcon(taskData.type || TASK_TYPE.MAINTENANCE)}
                        label={t(`taskType.${taskData.type || TASK_TYPE.MAINTENANCE}`)}
                        variant="outlined"
                        size="small" 
                      />
                    </Box>
                  </Box>
                  {taskData.estimatedHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {t('createTask.fields.estimatedHours')}: {taskData.estimatedHours}
                      </Typography>
                    </Box>
                  )}
                  {taskData.actualHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {t('taskDetail.actualHours')}: {taskData.actualHours}
                      </Typography>
                    </Box>
                  )}
                  {taskData.createdAt && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('taskDetail.createdAt')}: {new Date(taskData.createdAt).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {renderLabourCostSection()}

              {renderFinancialTracking()}
            </Grid>
          </Grid>
        </Paper>
        
        {/* Comments Section */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CommentIcon sx={{ mr: 1 }} />
              {t('taskDetail.comments')}
            </Typography>
            <Divider />
          </Box>
          
          {/* Comment List */}
          <List sx={{ mb: 3 }}>
            {taskData?.notes && taskData.notes.length > 0 ? (
              [...taskData.notes]
                .sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))
                .map((note, index) => (
                  <ListItem 
                    key={index} 
                    alignItems="flex-start"
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: note.addedBy?._id === taskData.assignedTo?._id ? 'rgba(25, 118, 210, 0.1)' : 'background.paper',
                      border: '1px solid',
                      borderColor: note.addedBy?._id === taskData.assignedTo?._id ? 'primary.main' : 'divider'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={!imageErrors[note.addedBy?._id] && note.addedBy?.imageUrl ? note.addedBy.imageUrl : ''}
                        onError={() => handleImageError(note.addedBy?._id)}
                        sx={{ 
                          bgcolor: note.addedBy?._id === taskData.assignedTo?._id ? 'primary.main' : 'grey.500',
                          width: 40,
                          height: 40
                        }}
                      >
                        {note.addedBy?.name 
                          ? note.addedBy.name[0].toUpperCase() 
                          : note.addedBy?.username 
                            ? note.addedBy.username[0].toUpperCase()
                            : <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {note.addedBy ? (
                              <>
                                {note.addedBy.name || note.addedBy.username || t('common.unknown')}
                                {note.addedBy._id === taskData.assignedTo?._id && (
                                  <Chip 
                                    label={t('taskDetail.assignee')} 
                                    size="small" 
                                    color="primary" 
                                    sx={{ ml: 1, height: 20 }} 
                                  />
                                )}
                                {note.addedBy._id === user?._id && (
                                  <Chip 
                                    label={t('taskDetail.you')} 
                                    size="small" 
                                    color="secondary" 
                                    sx={{ ml: 1, height: 20 }} 
                                  />
                                )}
                              </>
                            ) : t('common.unknown')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(note.addedAt), 'PPpp')}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mt: 1,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {note.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">
                  {t('taskDetail.noComments')}
                </Typography>
              </Box>
            )}
          </List>
          
          {/* Add Comment Form */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: 2
          }}>
            <Avatar 
              src={!imageErrors[user?._id] && user?.imageUrl ? user.imageUrl : ''}
              onError={() => handleImageError(user?._id)}
              sx={{ 
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                alignSelf: { xs: 'center', sm: 'flex-start' },
                mb: { xs: 1, sm: 0 }
              }}
            >
              {user?.name 
                ? user.name[0].toUpperCase() 
                : user?.username 
                  ? user.username[0].toUpperCase()
                  : <PersonIcon />}
            </Avatar>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('taskDetail.addComment')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={handleCommentKeyDown}
              variant="outlined"
              disabled={submittingComment}
              helperText={t('taskDetail.enterToSend') + ' • ' + t('common:lineBreaksPreserved', 'Line breaks will be preserved')}
            />
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              onClick={handleAddComment}
              disabled={!comment.trim() || submittingComment}
              sx={{ 
                mt: 1,
                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              {t('taskDetail.send')}
            </Button>
          </Box>
        </Paper>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            {t('taskDetail.deleteConfirmation')}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {t('taskDetail.deleteWarning')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteTask} 
              color="error" 
              variant="contained"
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TaskDetailPage; 